import api from './api';
import {
  SurveyRepository,
  EnvironmentRepository,
  ItemRepository,
  FileRepository,
  SyncQueueManager,
} from './database/index.js';

const ENTITY_TYPES = SyncQueueManager.ENTITY_TYPES;
const ACTIONS = SyncQueueManager.ACTIONS;

const isOnline = () => navigator.onLine;

const normalizeResponse = (data) => ({ data });

const surveyService = {
  // ===== NETWORK STATUS =====

  isOnline,

  // ===== CRUD OPERATIONS (OFFLINE-FIRST) =====

  getAllSurveys: async (params = {}) => {
    if (!isOnline()) {
      console.log('[Offline] Buscando levantamentos do IndexedDB');
      const surveys = await SurveyRepository.getAll({ syncStatus: params.syncStatus });
      return {
        success: true,
        data: surveys,
        pagination: { page: 1, limit: surveys.length, total: surveys.length, pages: 1 }
      };
    }

    try {
      const response = await api.get('/surveys', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar levantamentos:', error);
      throw error;
    }
  },

  getSurveyById: async (id) => {
    if (!isOnline()) {
      console.log('[Offline] Buscando levantamento por ID do IndexedDB');
      const local = await SurveyRepository.getById(id);
      if (local) return { success: true, data: local };
      const byLocalId = await SurveyRepository.getByLocalId(id);
      if (byLocalId) return { success: true, data: byLocalId };
      throw new Error('Levantamento não encontrado offline');
    }

    try {
      const response = await api.get(`/surveys/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar levantamento:', error);
      throw error;
    }
  },

  createSurvey: async (surveyData) => {
    if (!isOnline()) {
      console.log('[Offline] Criando levantamento no IndexedDB');
      const survey = await SurveyRepository.create(surveyData);
      await SyncQueueManager.enqueue(ENTITY_TYPES.SURVEY, survey.id, ACTIONS.CREATE, survey);
      return {
        success: true,
        data: {
          ...survey,
          localId: survey.localId,
        }
      };
    }

    try {
      const response = await api.post('/surveys', surveyData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar levantamento:', error);
      throw error;
    }
  },

  updateSurvey: async (id, updateData) => {
    if (!isOnline()) {
      console.log('[Offline] Atualizando levantamento no IndexedDB');
      const survey = await SurveyRepository.update(id, updateData);
      await SyncQueueManager.enqueue(ENTITY_TYPES.SURVEY, id, ACTIONS.UPDATE, { id, ...updateData });
      return { success: true, data: survey };
    }

    try {
      const response = await api.put(`/surveys/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar levantamento:', error);
      throw error;
    }
  },

  deleteSurvey: async (id) => {
    if (!isOnline()) {
      console.log('[Offline] Deletando levantamento do IndexedDB');
      await SurveyRepository.remove(id);
      await SyncQueueManager.enqueue(ENTITY_TYPES.SURVEY, id, ACTIONS.DELETE, { id });
      return { success: true };
    }

    try {
      const response = await api.delete(`/surveys/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao deletar levantamento:', error);
      throw error;
    }
  },

  // ===== ENVIRONMENTS (OFFLINE-FIRST) =====

  getEnvironments: async (surveyId) => {
    if (!isOnline()) {
      console.log('[Offline] Buscando ambientes do IndexedDB');
      const environments = await EnvironmentRepository.getBySurveyLocalId(surveyId);
      return { success: true, environments };
    }

    try {
      const response = await api.get(`/surveys/${surveyId}/environments`);
      return response.data;
    } catch (error) {
      console.error('Erro ao listar ambientes:', error);
      throw error;
    }
  },

  createEnvironment: async (surveyId, environmentData) => {
    if (!isOnline()) {
      console.log('[Offline] Criando ambiente no IndexedDB');
      const environment = await EnvironmentRepository.create(surveyId, null, environmentData);
      await SyncQueueManager.enqueue(ENTITY_TYPES.ENVIRONMENT, environment.id, ACTIONS.CREATE, environment);
      return { success: true, environment };
    }

    try {
      const response = await api.post(`/surveys/${surveyId}/environments`, environmentData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar ambiente:', error);
      throw error;
    }
  },

  getEnvironmentById: async (surveyId, environmentId) => {
    if (!isOnline()) {
      const local = await EnvironmentRepository.getById(environmentId);
      const environment = local || await EnvironmentRepository.getByLocalId(environmentId);
      if (!environment) throw new Error('Ambiente não encontrado offline');
      const items = await ItemRepository.getByEnvironmentLocalId(environment.localId);
      return { success: true, environment, items };
    }

    try {
      const response = await api.get(`/surveys/${surveyId}/environments/${environmentId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar ambiente:', error);
      throw error;
    }
  },

  updateEnvironment: async (surveyId, environmentId, environmentData) => {
    if (!isOnline()) {
      const environment = await EnvironmentRepository.update(environmentId, environmentData);
      await SyncQueueManager.enqueue(ENTITY_TYPES.ENVIRONMENT, environmentId, ACTIONS.UPDATE, { id: environmentId, ...environmentData });
      return { success: true, environment };
    }

    try {
      const response = await api.put(`/surveys/${surveyId}/environments/${environmentId}`, environmentData);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar ambiente:', error);
      throw error;
    }
  },

  deleteEnvironment: async (surveyId, environmentId) => {
    if (!isOnline()) {
      await EnvironmentRepository.remove(environmentId);
      await SyncQueueManager.enqueue(ENTITY_TYPES.ENVIRONMENT, environmentId, ACTIONS.DELETE, { id: environmentId });
      return { success: true };
    }

    try {
      const response = await api.delete(`/surveys/${surveyId}/environments/${environmentId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao excluir ambiente:', error);
      throw error;
    }
  },

  // ===== ITEMS (OFFLINE-FIRST) =====

  getEnvironmentItems: async (surveyId, environmentId) => {
    if (!isOnline()) {
      const items = await ItemRepository.getByEnvironmentLocalId(environmentId);
      return { success: true, items };
    }

    try {
      const response = await api.get(`/surveys/${surveyId}/environments/${environmentId}/items`);
      return response.data;
    } catch (error) {
      console.error('Erro ao listar itens:', error);
      throw error;
    }
  },

  createEnvironmentItem: async (surveyId, environmentId, localEnvironmentId, itemData) => {
    if (!isOnline()) {
      const item = await ItemRepository.create(environmentId, localEnvironmentId, itemData);
      await SyncQueueManager.enqueue(ENTITY_TYPES.ITEM, item.id, ACTIONS.CREATE, item);
      return { success: true, item };
    }

    try {
      const response = await api.post(`/surveys/${surveyId}/environments/${environmentId}/items`, itemData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar item:', error);
      throw error;
    }
  },

  updateEnvironmentItem: async (surveyId, environmentId, itemId, itemData) => {
    if (!isOnline()) {
      const item = await ItemRepository.update(itemId, itemData);
      await SyncQueueManager.enqueue(ENTITY_TYPES.ITEM, itemId, ACTIONS.UPDATE, { id: itemId, ...itemData });
      return { success: true, item };
    }

    try {
      const response = await api.put(`/surveys/${surveyId}/environments/${environmentId}/items/${itemId}`, itemData);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar item:', error);
      throw error;
    }
  },

  deleteEnvironmentItem: async (surveyId, environmentId, itemId) => {
    if (!isOnline()) {
      await ItemRepository.remove(itemId);
      await SyncQueueManager.enqueue(ENTITY_TYPES.ITEM, itemId, ACTIONS.DELETE, { id: itemId });
      return { success: true };
    }

    try {
      const response = await api.delete(`/surveys/${surveyId}/environments/${environmentId}/items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao excluir item:', error);
      throw error;
    }
  },

  // ===== FILES (ONLINE-ONLY por enquanto, requiere upload) =====

  uploadFiles: async (surveyId, files, fileData) => {
    if (!isOnline()) {
      console.log('[Offline] Arquivos serão sincronizados quando online');
      const savedFiles = [];
      for (const file of files) {
        const fileRecord = await FileRepository.create(surveyId, surveyId, {
          ...fileData,
          filename: file.name,
          size: file.size,
          type: file.type,
        });
        await FileRepository.saveBlob(fileRecord.localId, file);
        await SyncQueueManager.enqueue(ENTITY_TYPES.FILE, fileRecord.id, ACTIONS.CREATE, fileRecord);
        savedFiles.push(fileRecord);
      }
      return { success: true, files: savedFiles };
    }

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      if (fileData.file_type) formData.append('file_type', fileData.file_type);
      if (fileData.category) formData.append('category', fileData.category);
      if (fileData.description) formData.append('description', fileData.description);
      if (fileData.environment_id) formData.append('environment_id', fileData.environment_id);

      const response = await api.post(`/surveys/${surveyId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 minutos para uploads pesados
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao fazer upload de arquivos:', error);
      throw error;
    }
  },

  deleteFile: async (fileId) => {
    if (!isOnline()) {
      await FileRepository.remove(fileId);
      await SyncQueueManager.enqueue(ENTITY_TYPES.FILE, fileId, ACTIONS.DELETE, { id: fileId });
      return { success: true };
    }

    try {
      const response = await api.delete(`/surveys/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao deletar arquivo:', error);
      throw error;
    }
  },

  getSurveyFiles: async (surveyId) => {
    if (!isOnline()) {
      const localFiles = await FileRepository.getBySurveyLocalId(surveyId);
      return { success: true, files: localFiles };
    }

    try {
      const response = await api.get(`/surveys/${surveyId}/files`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar arquivos:', error);
      throw error;
    }
  },

  // ===== STATS =====

  getSurveyStats: async (params = {}) => {
    if (!isOnline()) {
      const all = await SurveyRepository.getAll();
      return {
        total: all.length,
        pending: all.filter(s => s.syncStatus === 'pending').length,
        synced: all.filter(s => s.syncStatus === 'synced').length,
      };
    }

    try {
      const response = await api.get('/surveys/stats', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  },

  // ===== PROJECT 3D =====

  upload3DFiles: async (surveyId, files, description) => {
    if (!isOnline()) {
      throw new Error('Upload de arquivos 3D requer conexão online');
    }

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      if (description) formData.append('description', description);

      const response = await api.post(`/surveys/${surveyId}/files-3d`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 minutos para uploads pesados de 3D/PDF
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao fazer upload de arquivos 3D:', error);
      throw error;
    }
  },

  uploadTemplate3D: async (surveyId, files, description) => {
    if (!isOnline()) {
      throw new Error('Upload de template 3D requer conexão online');
    }

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      if (description) formData.append('description', description);

      const response = await api.post(`/surveys/${surveyId}/template-3d`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 minutos
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao fazer upload de template 3D:', error);
      throw error;
    }
  },

  update3DObservations: async (surveyId, data) => {
    if (!isOnline()) {
      throw new Error('Atualização 3D requer conexão online');
    }

    try {
      const response = await api.put(`/surveys/${surveyId}/project-3d`, data);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar observações 3D:', error);
      throw error;
    }
  },

  get3DAnalytics: async (surveyId) => {
    if (!isOnline()) {
      throw new Error('Analytics requer conexão online');
    }

    try {
      const response = await api.get(`/surveys/${surveyId}/3d-analytics`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar analytics 3D:', error);
      throw error;
    }
  },

  // ===== PRESENTATION & BUDGET =====

  createPresentationAppointment: async (surveyId, appointmentData) => {
    if (!isOnline()) {
      throw new Error('Agendamento de apresentação requer conexão online');
    }

    try {
      const response = await api.post(`/surveys/${surveyId}/presentation-appointment`, appointmentData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar agendamento de apresentação:', error);
      throw error;
    }
  },

  approveOrRejectByClient: async (surveyId, action) => {
    if (!isOnline()) {
      throw new Error('Aprovação pelo cliente requer conexão online');
    }

    try {
      const response = await api.post(`/surveys/${surveyId}/approve-reject-client`, { action });
      return response.data;
    } catch (error) {
      console.error('Erro ao aprovar/reprovar levantamento pelo cliente:', error);
      throw error;
    }
  },

  approveSurveyDirect: async (surveyId) => {
    if (!isOnline()) {
      throw new Error('Aprovação direta requer conexão online');
    }

    try {
      const response = await api.post(`/surveys/${surveyId}/approve-direct`);
      return response.data;
    } catch (error) {
      console.error('Erro ao aprovar levantamento diretamente:', error);
      throw error;
    }
  },

  generateBudgetFromSurvey: async (surveyId) => {
    if (!isOnline()) {
      throw new Error('Geração de orçamento requer conexão online');
    }

    try {
      const response = await api.post(`/surveys/${surveyId}/generate-budget`);
      return response.data;
    } catch (error) {
      console.error('Erro ao gerar orçamento:', error);
      throw error;
    }
  },

  approveBudget: async (surveyId, { approved, notes }) => {
    if (!isOnline()) {
      throw new Error('Aprovação de orçamento requer conexão online');
    }

    try {
      const response = await api.post(`/surveys/${surveyId}/approve-budget`, { approved, notes });
      return response.data;
    } catch (error) {
      console.error('Erro ao processar aprovação:', error);
      throw error;
    }
  },

  // ===== FORMATTERS =====

  formatSurveyCode: (code) => {
    if (!code) return 'N/A';
    return code.replace('LEV-', '').replace('-', '/');
  },

  formatStatus: (status) => {
    const statusMap = {
      'DRAFT': 'Rascunho',
      'RASCUNHO': 'Rascunho',
      'VISITA_REALIZADA': 'Visita Realizada',
      'ORCAMENTO_APROVADO': 'Orçamento Aprovado',
      'EM_PROJETACAO': 'Em Projetação',
      'EM_REVISAO': 'Em Revisão',
      'APRESENTACAO_AGENDADA': 'Apresentação Agendada',
      'APRESENTADO_CLIENTE': 'Apresentado ao Cliente',
      'ORCAMENTO_PRONTO': 'Orçamento Pronto',
      'ORCAMENTO_REPROVADO': 'Orçamento Reprovado',
      'ARCHIVED': 'Arquivado'
    };
    return statusMap[status] || status;
  },

  formatPriority: (priority) => {
    const priorityMap = { 'LOW': 'Baixa', 'NORMAL': 'Normal', 'HIGH': 'Alta', 'URGENT': 'Urgente' };
    return priorityMap[priority] || priority;
  },

  getStatusColor: (status) => {
    const colorMap = {
      'DRAFT': 'bg-yellow-100 text-yellow-800',
      'RASCUNHO': 'bg-yellow-100 text-yellow-800',
      'VISITA_REALIZADA': 'bg-blue-100 text-blue-800',
      'EM_PROJETACAO': 'bg-purple-100 text-purple-800',
      'EM_REVISAO': 'bg-orange-100 text-orange-800',
      'ORCAMENTO_APROVADO': 'bg-green-100 text-green-800',
      'ARCHIVED': 'bg-gray-100 text-gray-800',
      'APRESENTADO_CLIENTE': 'bg-green-100 text-green-800',
      'ORCAMENTO_PRONTO': 'bg-emerald-100 text-emerald-800',
      'ORCAMENTO_REPROVADO': 'bg-red-100 text-red-800',
      'APRESENTACAO_AGENDADA': 'bg-pink-100 text-pink-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  },

  getPriorityColor: (priority) => {
    const colorMap = {
      'LOW': 'bg-gray-100 text-gray-800',
      'NORMAL': 'bg-blue-100 text-blue-800',
      'HIGH': 'bg-orange-100 text-orange-800',
      'URGENT': 'bg-red-100 text-red-800'
    };
    return colorMap[priority] || 'bg-gray-100 text-gray-800';
  },

  getComplexityLabel: (level) => {
    const labels = { 1: 'Muito Simples', 2: 'Simples', 3: 'Médio', 4: 'Complexo', 5: 'Muito Complexo' };
    return labels[level] || 'Não definido';
  },

  getComplexityColor: (level) => {
    const colors = {
      1: 'bg-green-100 text-green-800',
      2: 'bg-blue-100 text-blue-800',
      3: 'bg-yellow-100 text-yellow-800',
      4: 'bg-orange-100 text-orange-800',
      5: 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  },

  formatDate: (dateString) => {
    if (!dateString) return 'Não definido';
    if (typeof dateString === 'string' && dateString.includes('T')) {
      const [year, month, day] = dateString.split('T')[0].split('-');
      return `${day}/${month}/${year}`;
    }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch { return 'Data inválida'; }
  },

  formatDateTime: (dateString) => {
    if (!dateString) return 'Não definido';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR');
    } catch { return 'Data inválida'; }
  },

  getFileIcon: (mimeType) => {
    if (!mimeType) return '📎';
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.includes('word')) return '📝';
    if (mimeType === 'text/plain') return '📄';
    return '📎';
  },

  formatFileSize: (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // ===== VALIDATION =====

  validateSurveyData: (data) => {
    const errors = {};
    if (!data.client_name || data.client_name.trim() === '') errors.client_name = 'Nome do cliente é obrigatório';
    if (data.client_document_id && data.client_document_id.trim() !== '') {
      const docClean = data.client_document_id.replace(/\D/g, '');
      if (docClean.length !== 11 && docClean.length !== 14) errors.client_document_id = 'CPF/CNPJ inválido';
    }
    if (!data.client_address || data.client_address.trim() === '') errors.client_address = 'Endereço é obrigatório';
    if (data.client_phone && data.client_phone.trim() !== '') {
      const phoneClean = data.client_phone.replace(/\D/g, '');
      if (phoneClean.length < 10 || phoneClean.length > 11) errors.client_phone = 'Telefone inválido';
    }
    if (data.client_email && data.client_email.trim() !== '') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.client_email)) errors.client_email = 'Email inválido';
    }
    if (data.status === 'EM_REVISAO' && (!data.return_reason || data.return_reason.trim() === '')) {
      errors.return_reason = 'Informe o motivo da devolução';
    }
    return { isValid: Object.keys(errors).length === 0, errors };
  },

  // ===== OPTIONS =====

  getStatusOptions: () => [
    { value: '', label: 'Todos os status' },
    { value: 'RASCUNHO', label: 'Rascunho' },
    { value: 'VISITA_REALIZADA', label: 'Visita Realizada' },
    { value: 'EM_PROJETACAO', label: 'Em Projetação' },
    { value: 'EM_REVISAO', label: 'Em Revisão' },
    { value: 'APRESENTACAO_AGENDADA', label: 'Apresentação Agendada' },
    { value: 'APRESENTADO_CLIENTE', label: 'Apresentado ao Cliente' },
    { value: 'ORCAMENTO_PRONTO', label: 'Orçamento Pronto' },
    { value: 'ORCAMENTO_REPROVADO', label: 'Orçamento Reprovado' },
    { value: 'ORCAMENTO_APROVADO', label: 'Orçamento Aprovado' },
    { value: 'ARCHIVED', label: 'Arquivado' }
  ],

  getPriorityOptions: () => [
    { value: '', label: 'Todas as prioridades' },
    { value: 'LOW', label: 'Baixa' },
    { value: 'NORMAL', label: 'Normal' },
    { value: 'HIGH', label: 'Alta' },
    { value: 'URGENT', label: 'Urgente' }
  ],

  getComplexityOptions: () => [
    { value: '', label: 'Todos os níveis' },
    { value: 1, label: 'Muito Simples' },
    { value: 2, label: 'Simples' },
    { value: 3, label: 'Médio' },
    { value: 4, label: 'Complexo' },
    { value: 5, label: 'Muito Complexo' }
  ],

  getFileTypeOptions: () => [
    { value: 'TABLET', label: 'Arquivo do Tablet' },
    { value: 'PHOTO', label: 'Foto' },
    { value: 'DOCUMENT', label: 'Documento' },
    { value: 'PROJECT_3D', label: 'Projeto 3D' },
    { value: 'OTHER', label: 'Outro' }
  ],

  get3DStatusOptions: (userRole = '') => {
    if (userRole === 'RECEPCAO_PROJETOS') {
      return [
        { value: 'IN_PROGRESS', label: 'Em Progresso' },
        { value: 'COMPLETED', label: 'Concluído' }
      ];
    }
    const allOptions = [
      { value: 'PENDING', label: 'Pendente' },
      { value: 'IN_PROGRESS', label: 'Em Progresso' },
      { value: 'COMPLETED', label: 'Concluído' },
      { value: 'REVIEW', label: 'Em Revisão' },
      { value: 'APPROVED', label: 'Aprovado' }
    ];
    return allOptions;
  },

  format3DStatus: (status) => {
    const statusMap = {
      'PENDING': 'Pendente',
      'IN_PROGRESS': 'Em Progresso',
      'COMPLETED': 'Concluído',
      'REVIEW': 'Em Revisão',
      'APPROVED': 'Aprovado'
    };
    return statusMap[status] || status;
  },

  get3DStatusColor: (status) => {
    const colorMap = {
      'PENDING': 'bg-gray-100 text-gray-800',
      'IN_PROGRESS': 'bg-blue-100 text-blue-800',
      'COMPLETED': 'bg-green-100 text-green-800',
      'REVIEW': 'bg-yellow-100 text-yellow-800',
      'APPROVED': 'bg-purple-100 text-purple-800',
      'APRESENTACAO': 'bg-pink-100 text-pink-800',
      'APRESENTADO': 'bg-green-100 text-green-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  },

  // ===== SYNC STATUS =====

  getSyncStatus: async () => {
    const pendingSurveys = await SurveyRepository.countPending();
    const pendingQueue = await SyncQueueManager.countPending();
    return {
      online: isOnline(),
      pendingSurveys,
      pendingQueue,
      totalPending: pendingSurveys + pendingQueue,
    };
  },

  // ===== REPOSITORY ACCESS (for advanced use) =====

  repositories: {
    surveys: SurveyRepository,
    environments: EnvironmentRepository,
    items: ItemRepository,
    files: FileRepository,
    syncQueue: SyncQueueManager,
  },

  // ===== BUDGET SHEETS =====

  getBudgetSheetsBySurvey: async (surveyId) => {
    try {
      const response = await api.get(`/surveys/${surveyId}/budget-sheets`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar fichas de orçamento:', error);
      throw error;
    }
  },

  getBudgetSheetById: async (surveyId, sheetId) => {
    try {
      const response = await api.get(`/surveys/${surveyId}/budget-sheets/${sheetId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar ficha de orçamento:', error);
      throw error;
    }
  },

  createOrUpdateBudgetSheet: async (surveyId, data) => {
    try {
      const response = await api.post(`/surveys/${surveyId}/budget-sheets`, data);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar/atualizar ficha de orçamento:', error);
      throw error;
    }
  },

  deleteBudgetSheet: async (surveyId, sheetId) => {
    try {
      const response = await api.delete(`/surveys/${surveyId}/budget-sheets/${sheetId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao excluir ficha de orçamento:', error);
      throw error;
    }
  },

  getBudgetSheetOptions: async () => {
    try {
      const response = await api.get('/surveys/budget-sheets/options');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar opções de ficha:', error);
      throw error;
    }
  },

  getBudgetSheetModels: async () => {
    try {
      const response = await api.get('/surveys/budget-sheets/models');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar modelos de ficha:', error);
      throw error;
    }
  },

  getAvailableProducts: async (params) => {
    try {
      const response = await api.get('/surveys/budget-sheets/products', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      throw error;
    }
  },

  finalizePresentation: async (surveyId) => {
    try {
      const response = await api.post(`/surveys/${surveyId}/finalize-presentation`);
      return response.data;
    } catch (error) {
      console.error('Erro ao finalizar apresentação:', error);
      throw error;
    }
  },
};

export default surveyService;
